/**
 * Pure geometry for radiological L/R display normalization (web-demo-frontend,
 * case-87 laterality fix). DICOM IOP = [rowX,rowY,rowZ, colX,colY,colZ];
 * row cosine = screen left→right direction; patient +X = LEFT, +Y = POSTERIOR,
 * +Z = HEAD. Radiological convention = patient-left on the viewer's RIGHT.
 *
 * Cornerstone3D's `OrientationAxis.ACQUISITION` camera comes straight from the
 * series' IOP (viewUp = -columnCosine, viewRight = rowCosine), so a series
 * stored with a mirrored row direction renders left/right reversed relative to
 * a sibling stored the standard way. We correct it with a display-only
 * horizontal camera flip — never a geometry/volume-direction change, which
 * would desync the shared FrameOfReferenceUID world coords that the crosshair
 * and AI-jump sync rely on.
 */
import { logger } from "@/utils/logger";
import type { PlaneOrientation } from "@/viewport/types";

export type Vec3 = [number, number, number];

/** Anatomical letters for the four viewport edges. */
export interface OrientationLabels {
  top: string;
  bottom: string;
  left: string;
  right: string;
}

export function parseIop(
  iop: ArrayLike<number> | null | undefined,
): { row: Vec3; col: Vec3 } | null {
  if (!iop || iop.length < 6) return null;
  for (let i = 0; i < 6; i++) if (!Number.isFinite(iop[i])) return null;
  return {
    row: [iop[0], iop[1], iop[2]],
    col: [iop[3], iop[4], iop[5]],
  };
}

/** Index of the patient axis a vector points most strongly along, or null when
 *  the vector is degenerate. */
function dominantAxis(v: Vec3): 0 | 1 | 2 | null {
  let ax: 0 | 1 | 2 = 0;
  let best = -Infinity;
  for (const i of [0, 1, 2] as const) {
    if (Math.abs(v[i]) > best) {
      best = Math.abs(v[i]);
      ax = i;
    }
  }
  return best < 1e-6 ? null : ax;
}

/**
 * Which patient side lands on the viewer's RIGHT, from the row cosine (the
 * image's left→right direction). "right" = patient-left on the right =
 * radiological (correct). "left" = neurological (mirrored, needs flip).
 *
 * "none" when L/R is not the row's DOMINANT axis (sagittal, or an oblique whose
 * horizontal is mostly A/P or H/F): a horizontal flip there mirrors an axis
 * that isn't laterality. Dominance, not a magnitude threshold — the same argmax
 * `dominantLetter` uses, so the flip decision and the letters can never
 * disagree about which patient axis is horizontal.
 */
export function patientLeftScreenSide(rowCosine: Vec3): "right" | "left" | "none" {
  if (dominantAxis(rowCosine) !== 0) return "none";
  return rowCosine[0] > 0 ? "right" : "left";
}

/**
 * Whether a series rendered on its ACQUISITION camera must be flipped to reach
 * radiological convention.
 *
 * The decision is PURELY GEOMETRIC — the row cosine, never the `plane` string.
 * There used to be an `Axial`/`Coronal` gate here, and it was a latent
 * correctness hole rather than a safeguard:
 *
 *   - It could not add safety. `patientLeftScreenSide` already returns "none"
 *     for any row whose dominant axis isn't X, which is exactly the sagittal and
 *     oblique-horizontal set the gate was meant to exclude.
 *   - It could subtract correctness. `derivePlane` trusts the backend
 *     `meta.plane` string first, and the backend fills it from series-DESCRIPTION
 *     tokens when IOP is absent (`dicom_utils.py`). A series described "SAG" but
 *     stored with an X-dominant-negative row therefore got no flip and no log —
 *     pixels left mirrored, which is the bug this module exists to prevent.
 *
 * `plane` is retained as a DIAGNOSTIC only: when the geometry says "flip" and the
 * plane string claims something without in-plane laterality, that disagreement
 * means the string is wrong, and it is worth surfacing rather than obeying.
 *
 * Call sites should prefer `paneNeedsRadiologicalFlip`, which also accounts for
 * MPR reslice panes.
 */
export function needsRadiologicalFlip(
  iop: ArrayLike<number> | null | undefined,
  plane: PlaneOrientation | null | undefined,
): boolean {
  const parsed = parseIop(iop);
  if (!parsed) return false;
  if (patientLeftScreenSide(parsed.row) !== "left") return false;
  if (plane && plane !== "Axial" && plane !== "Coronal") {
    logger.warn(
      "[radiologicalOrientation] plane string %o disagrees with geometry: row cosine is " +
        "X-dominant (in-plane laterality) and mirrored. Trusting the IOP and flipping. " +
        "Check the backend plane derivation for this series.",
      plane,
    );
  }
  return true;
}

/**
 * ImageOrientationPatient of an MPR reformat plane, as Cornerstone builds it:
 * canonical patient axes, independent of the source series' own IOP.
 * [rowX, rowY, rowZ, colX, colY, colZ] — row = screen-right, col = screen-down.
 *
 * Mirrors `mprCameraValues` in @cornerstonejs/core (axial/coronal viewRight
 * [1,0,0], sagittal viewRight [0,1,0]; viewUp [0,-1,0] / [0,0,1]).
 *
 * Axial is included for totality — a reslice target's plane is the full plane
 * union even though the planner only ever emits Coronal/Sagittal — so a
 * degenerate axial→axial reslice labels correctly instead of falling off a
 * partial lookup into `undefined`.
 */
export const RESLICE_PLANE_IOP: Record<PlaneOrientation, readonly number[]> = {
  Axial: [1, 0, 0, 0, 1, 0],
  Coronal: [1, 0, 0, 0, 0, -1],
  Sagittal: [0, 1, 0, 0, 0, -1],
};

/** What a single pane displays: its series' stored IOP and native plane, plus
 *  the MPR plane it is resliced to (undefined for a normal pane). */
export interface PaneOrientation {
  nativeIop: ArrayLike<number> | null | undefined;
  nativePlane: PlaneOrientation | null | undefined;
  reslicePlane?: PlaneOrientation;
}

/**
 * Whether THIS PANE needs the flip — the reslice-aware form, and the one every
 * call site should use.
 *
 * A reslice (MPR companion) pane renders the shared source volume through
 * Cornerstone's canonical patient-space camera (`OrientationAxis.CORONAL` /
 * `SAGITTAL` → `mprCameraValues`, viewRight `[1,0,0]` / `[0,1,0]`). That is
 * radiological by construction and inherits NONE of the source series'
 * handedness, so applying the source's flip would mirror an already-correct
 * reformat — reintroducing the very axial-vs-coronal disagreement this module
 * exists to fix, and putting an A/P mirror on the sagittal pane.
 */
export function paneNeedsRadiologicalFlip(pane: PaneOrientation): boolean {
  if (pane.reslicePlane) return false;
  return needsRadiologicalFlip(pane.nativeIop, pane.nativePlane);
}

/** The IOP of the geometry a pane actually displays: the reformat plane's
 *  canonical axes for a reslice pane, the series' stored IOP otherwise.
 *
 *  Third case, and the reason this isn't just the two lines it looks like: a
 *  series with a KNOWN plane but NO stored IOP. That class is real and
 *  designed-for on the backend — `dicom_utils.py` derives `plane` from
 *  description tokens "so reformatted series with stripped IOP still get a usable
 *  plane", and `is_reconstructable.py` routes them to the stack path via
 *  `missing_iop`. Returning `null` there suppressed the overlay entirely, which
 *  was a silent REGRESSION against the plane-derived letters those panes show
 *  today.
 *
 *  We fall back to the plane's canonical radiological axes. That is an
 *  assumption, but it is exactly the assumption already shipping, and it is safe
 *  here for a specific reason: with no parseable IOP `needsRadiologicalFlip`
 *  returns false, so such a pane can NEVER be flipped — the letters cannot come
 *  to disagree with the pixels the way a flipped pane's could. Suppressing the
 *  overlay would remove the reader's only cross-check to avoid an inaccuracy that
 *  cannot occur. */
export function paneOrientationIop(pane: PaneOrientation): readonly number[] | null {
  if (pane.reslicePlane) return RESLICE_PLANE_IOP[pane.reslicePlane];
  if (pane.nativeIop) return Array.from(pane.nativeIop);
  if (pane.nativePlane) return RESLICE_PLANE_IOP[pane.nativePlane];
  return null;
}

const LETTER_POS: Record<0 | 1 | 2, [string, string]> = {
  // axis index -> [label for +dir, label for -dir]
  0: ["L", "R"], // +X patient LEFT / -X RIGHT
  1: ["P", "A"], // +Y POSTERIOR / -Y ANTERIOR
  2: ["H", "F"], // +Z HEAD / -Z FEET
};

function dominantLetter(v: Vec3): string {
  const ax = dominantAxis(v);
  if (ax === null) return "";
  return v[ax] >= 0 ? LETTER_POS[ax][0] : LETTER_POS[ax][1];
}

function neg(v: Vec3): Vec3 {
  return [-v[0], -v[1], -v[2]];
}

/**
 * Truthful edge labels for the ACTUAL displayed orientation. `flipApplied` is
 * the viewport's LIVE `flipHorizontal` — what the camera really has, not what
 * we asked for at load — so Reset View and the manual flip toggle cannot leave
 * the letters asserting the opposite of the pixels.
 *
 * screen-right patient direction = row cosine (negated if flipped);
 * screen-up patient direction = -col cosine (col points down the image).
 * Returns null when the IOP is unusable: on a SAFETY CRITICAL laterality
 * overlay, no letters beats letters that may be mirrored.
 */
export function deriveOrientationLabels(
  iop: ArrayLike<number> | null | undefined,
  flipApplied: boolean,
): OrientationLabels | null {
  const parsed = parseIop(iop);
  if (!parsed) return null;
  const screenRight: Vec3 = flipApplied ? neg(parsed.row) : parsed.row;
  const screenUp: Vec3 = neg(parsed.col);
  return {
    top: dominantLetter(screenUp),
    bottom: dominantLetter(neg(screenUp)),
    right: dominantLetter(screenRight),
    left: dominantLetter(neg(screenRight)),
  };
}

/** Minimal live-viewport shape (no hard @cornerstonejs/core dep), matching the
 *  pattern in src/viewport/cameraSnap.ts. */
export interface FlippableViewport {
  getCamera?: () => { flipHorizontal?: boolean } | null;
  setCamera?: (camera: Record<string, unknown>) => void;
  render?: () => void;
}

/**
 * Set `flipHorizontal` to radiological when `shouldFlip`. Idempotent, so it is
 * safe on every path that can clear the flip — series attach, context-loss
 * recovery, and Reset View (`resetCamera` unconditionally clears
 * `flipHorizontal`). `context` identifies the call site in the logs.
 *
 * Only ever SETS the flip, never clears it: `if (!shouldFlip) return false`
 * returns before touching the camera. That is sufficient rather than sloppy
 * because the one path that needs it cleared — `resetCamera` — clears it for us,
 * and every caller re-asserts afterwards.
 *
 * Returns true iff the flip is VERIFIED applied by this call. The read-back
 * matters: cornerstone's `Viewport.flip()` returns early and silently when
 * `getDefaultImageData()` is falsy, without touching `flipHorizontal`. Reporting
 * success from the fact that we called the setter would put a false confirmation
 * of a laterality correction into the log — the one place someone will look to
 * find out whether a mirrored study was corrected.
 */
export function applyRadiologicalFlip(
  vp: FlippableViewport | null | undefined,
  shouldFlip: boolean,
  context: string,
): boolean {
  if (!shouldFlip) return false;
  try {
    const cam = vp?.getCamera?.();
    if (!cam) {
      logger.warn("[radiologicalOrientation] flip skipped, no camera (%s)", context);
      return false;
    }
    if (cam.flipHorizontal === true) return false; // already radiological
    vp?.setCamera?.({ flipHorizontal: true });
    vp?.render?.();
    if (vp?.getCamera?.()?.flipHorizontal !== true) {
      logger.warn(
        "[radiologicalOrientation] flip did NOT take — setCamera returned without " +
          "applying flipHorizontal (%s). Pane is still in neurological convention; " +
          "the orientation letters will report the mirror.",
        context,
      );
      return false;
    }
    logger.info("[radiologicalOrientation] radiological L/R flip applied (%s)", context);
    return true;
  } catch (err) {
    logger.warn("[radiologicalOrientation] flip failed (%s):", context, err);
    return false;
  }
}
